import React, { useCallback, useState, useRef, useEffect } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { UserDropdownMenu } from "../user-dropdown-menu/user-dropdown-menu";
import { StatisticModal } from "../statistic-modal/statistic-modal";
import { UserMenuTrigger } from "../user-menu-trigger/user-menu-trigger";
import { CustomTypography } from "../custom-typography/custom-typography";
import { DayNightToggle } from "../day-night-toggle/day-night-toggle";
import { useAppStore } from "../../stores/appStore";
import useUserStore from "../../stores/userStore";

import { ChatInfoPanel } from "../chat-info-panel/chat-info-panel";
import { LogoDarkIcon, LogoLightIcon } from "../../assets/icon";
import { SettingsModal } from "../settings-modal/settings-modal";
import { useQuizeStore } from "../../stores/quizeStore";
import { useUpdateEffect } from "react-use";
import { getLogo } from "../../axios/logo";
import { useAuthenticatedFileUrl } from "../../hooks/useAuthenticatedFileUrl";
import { ManagerStatisticsModal } from "../manager-statistics-modal/manager-statistics-modal";
import { RecordingsModal } from "../recordings-modal/recordings-modal";
import { AssessmentHistoryModal } from "../assessment-history-modal/assessment-history-modal";
import { TeamAssessmentsModal } from "../team-assessments-modal/team-assessments-modal";

interface ChatHeaderProps {
  onLogout: () => void;
  onMessageSelect: (messageId: string) => void;
  onCollapseHeaderOnMessageSelect?: () => void;
}

interface LogoInfo {
  label: string;
  logo_light: string;
  logo_dark: string;
}

export const ChatHeader = ({
  onLogout,
  onCollapseHeaderOnMessageSelect,
}: ChatHeaderProps) => {
  const { setPreviousModalClosed, preassessmentData, fetchPreassessmentForce, toggleNeedCloseButton } = useQuizeStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoInfo, setLogoInfo] = useState<undefined | LogoInfo>(undefined);
  const [statisticModalOpen, setStatisticModalOpen] = useState(false);
  const [managerStatisticsModalOpen, setManagerStatisticsModalOpen] =
    useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [recordingsModalOpen, setRecordingsModalOpen] = useState(false);
  const [assessmentsHistoryModalOpen, setAssessmentsHistoryModalOpen] =
    useState(false);
  const [teamAssessmentsModalOpen, setTeamAssessmentsModalOpen] =
    useState(false);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
  const [infoPanelInitialTab, setInfoPanelInitialTab] = useState<string | null>(
    null,
  );
  const [infoPanelAnchorEl, setInfoPanelAnchorEl] =
    useState<HTMLElement | null>(null);
  const menuTriggerRef = useRef<HTMLDivElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    isDark,
    domain,
    focus,
    shouldOpenUnreadLessons,
    clearOpenUnreadLessons,
    isPreassessmentOpen,
    isAssessmentOpen,
    setIsUserMenuModalOpen,
  } = useAppStore();
  const getFocusDisplayName = useUserStore((state) => state.getFocusDisplayName);
  const focusDisplayName = getFocusDisplayName(focus, domain);
  const logoSrc = logoInfo
    ? isDark
      ? logoInfo.logo_dark
      : logoInfo.logo_light
    : undefined;
  const { url: resolvedLogoSrc } = useAuthenticatedFileUrl(logoSrc);

  const handleOpenUnreadLessonModal = useCallback(() => {
    setPreviousModalClosed(false);
    setInfoPanelInitialTab("Lessons to answer");
    setInfoPanelAnchorEl(menuTriggerRef.current);
    setIsInfoPanelOpen(true);
  }, [setPreviousModalClosed]);

  useEffect(() => {
    getLogo().then((data) => {
      if (data) setLogoInfo(data);
    });
  }, [domain]);

  useEffect(() => {
    if (shouldOpenUnreadLessons) {
      handleOpenUnreadLessonModal();
      clearOpenUnreadLessons();
    }
  }, [
    shouldOpenUnreadLessons,
    clearOpenUnreadLessons,
    handleOpenUnreadLessonModal,
  ]);

  useUpdateEffect(() => {
    if (preassessmentData.questions?.length) {
      setPreviousModalClosed(true);
      handleCloseSettingsModal();
    }
  }, [preassessmentData]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenStatisticModal = () => {
    setStatisticModalOpen(true);
    handleMenuClose();
  };

  const handleCloseStatisticModal = () => {
    setStatisticModalOpen(false);
  };

  const handleOpenManagerStatisticsModal = () => {
    setManagerStatisticsModalOpen(true);
    handleMenuClose();
  };

  const handleOpenAssessmentsHistoryModal = () => {
    setAssessmentsHistoryModalOpen(true);
    handleMenuClose();
  };

  const handleOpenMotivationalQuestionnaire = () => {
    if (isPreassessmentOpen || isAssessmentOpen) return;
    toggleNeedCloseButton();
    fetchPreassessmentForce();
    handleMenuClose();
  };

  const handleCloseAssessmentsHistoryModal = () => {
    setAssessmentsHistoryModalOpen(false);
  };

  const handleOpenTeamAssessmentsModal = () => {
    setTeamAssessmentsModalOpen(true);
    handleMenuClose();
  };

  const handleCloseTeamAssessmentsModal = () => {
    setTeamAssessmentsModalOpen(false);
  };

  const handleCloseManagerStatisticsModal = () => {
    setManagerStatisticsModalOpen(false);
  };

  const handleOpenSettingsModal = () => {
    setPreviousModalClosed(false);
    setSettingsModalOpen(true);
    handleMenuClose();
  };

  const handleCloseSettingsModal = () => {
    setSettingsModalOpen(false);
    setPreviousModalClosed(true);
  };

  const handleOpenChatInfoPanel = (
    initialTab?: "Lessons to answer" | "Crash Courses to answer",
  ) => {
    setPreviousModalClosed(false);
    setInfoPanelInitialTab(initialTab ?? null);
    setInfoPanelAnchorEl(anchorEl ?? menuTriggerRef.current);
    setIsInfoPanelOpen(true);
    handleMenuClose();
  };

  const handleOpenRecordingsModal = () => {
    if (isPreassessmentOpen || isAssessmentOpen) return;
    setRecordingsModalOpen(true);
    handleMenuClose();
  };

  const handleCloseRecordingsModal = () => {
    setRecordingsModalOpen(false);
  };

  const handleCloseInfoPanel = () => {
    setInfoPanelInitialTab(null);
    setInfoPanelAnchorEl(null);
    setIsInfoPanelOpen(false);
  };

  const isAnyUserMenuModalOpen =
    statisticModalOpen ||
    managerStatisticsModalOpen ||
    settingsModalOpen ||
    recordingsModalOpen ||
    assessmentsHistoryModalOpen ||
    teamAssessmentsModalOpen ||
    isInfoPanelOpen;

  useEffect(() => {
    setIsUserMenuModalOpen(isAnyUserMenuModalOpen);
  }, [isAnyUserMenuModalOpen, setIsUserMenuModalOpen]);

  const closeAllMenuWindows = useCallback(() => {
    handleMenuClose();
    setStatisticModalOpen(false);
    setManagerStatisticsModalOpen(false);
    setSettingsModalOpen(false);
    setRecordingsModalOpen(false);
    setAssessmentsHistoryModalOpen(false);
    setTeamAssessmentsModalOpen(false);
    setInfoPanelInitialTab(null);
    setInfoPanelAnchorEl(null);
    setIsInfoPanelOpen(false);
    setPreviousModalClosed(true);
  }, [setPreviousModalClosed]);

  useEffect(() => {
    const handleFeedbackSupportClicked = () => {
      closeAllMenuWindows();
    };

    window.addEventListener(
      "feedbackSupportClicked",
      handleFeedbackSupportClicked,
    );

    return () => {
      window.removeEventListener(
        "feedbackSupportClicked",
        handleFeedbackSupportClicked,
      );
    };
  }, [closeAllMenuWindows]);

  useEffect(() => {
    const handleOpenRecordingsModal = () => {
      if (isPreassessmentOpen || isAssessmentOpen) return;
      setRecordingsModalOpen(true);
    };

    window.addEventListener(
      "openRecordingsModal",
      handleOpenRecordingsModal,
    );

    return () => {
      window.removeEventListener(
        "openRecordingsModal",
        handleOpenRecordingsModal,
      );
    };
  }, [isPreassessmentOpen, isAssessmentOpen]);

  const truncateText = (text: string, maxLength: number = 20) => {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.header.bgColor,
        position: "relative",
        zIndex: 100,
      }}
    >
      <Box
        sx={{
          maxWidth: 1100,
          mx: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 1, sm: 2 },
          minWidth: 0,
          padding: { xs: "10px 16px", sm: "16px", md: "16px" },
          "@media (orientation: landscape) and (max-width: 960px)": {
            padding: "6px 16px",
          },
        }}
      >
        <Box
          sx={{
            fontWeight: "bold",
            color: "#333",
            display: "flex",
            alignItems: "center",
            gap: { xs: "8px", sm: "12px" },
            flex: "1 1 auto",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={
              domain === "Novartis"
                ? {
                    width: { xs: "48px", sm: "96px" },
                    height: { xs: "48px", sm: "96px" },
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                  }
                : {
                    width: { xs: "40px", sm: "48px" },
                    height: { xs: "40px", sm: "48px" },
                    flexShrink: 0,
                  }
            }
          >
            {logoInfo && resolvedLogoSrc ? (
              <img
                src={resolvedLogoSrc}
                alt="Logo"
              />
            ) : isDark ? (
              <LogoDarkIcon />
            ) : (
              <LogoLightIcon />
            )}
          </Box>
          <Box>
            <CustomTypography
              fontSize={{ xs: 12, sm: 14 }}
              color={theme.palette.header.titalColor}
              fontWeight={900}
              mr={{ xs: 2, sm: 0 }}
            >
              {logoInfo?.label ?? "COACHMATE"}
            </CustomTypography>
            <CustomTypography
              fontSize={{ xs: 12, sm: 14 }}
              color={theme.palette.header.titalColor}
              fontWeight={600}
            >
              {isMobile ? truncateText(domain) : domain}
            </CustomTypography>
            <CustomTypography
              fontSize={{ xs: 12, sm: 14 }}
              color={theme.palette.header.titalColor}
              fontWeight={600}
            >
              {isMobile ? truncateText(focusDisplayName) : focusDisplayName}
            </CustomTypography>
          </Box>
        </Box>
        <Box
          ref={menuTriggerRef}
          display="flex"
          gap={{ xs: 1, sm: "48px" }}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          {!isMobile && (
            <Box sx={{ flexShrink: 0 }}>
              <DayNightToggle />
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: "0 1 auto" }}>
            <UserMenuTrigger onClick={handleMenuOpen} isMenuOpen={isMenuOpen} />
          </Box>
        </Box>
      </Box>

      <UserDropdownMenu
        anchorEl={anchorEl}
        isOpen={isMenuOpen}
        onClose={handleMenuClose}
        onLogout={onLogout}
        onStatisticOpen={handleOpenStatisticModal}
        onSettingsOpen={handleOpenSettingsModal}
        onRecordingsOpen={handleOpenRecordingsModal}
        onChatInfoOpen={handleOpenChatInfoPanel}
        onManagerStatisticsOpen={handleOpenManagerStatisticsModal}
        onAssessmentsHistoryOpen={handleOpenAssessmentsHistoryModal}
        onTeamAssessmentsOpen={handleOpenTeamAssessmentsModal}
        onMotivationalQuestionnaireOpen={handleOpenMotivationalQuestionnaire}
      />

      <StatisticModal
        open={statisticModalOpen}
        onClose={handleCloseStatisticModal}
      />

      <SettingsModal
        open={settingsModalOpen}
        onClose={handleCloseSettingsModal}
      />

      <ManagerStatisticsModal
        open={managerStatisticsModalOpen}
        onClose={handleCloseManagerStatisticsModal}
      />

      <RecordingsModal
        open={recordingsModalOpen}
        onClose={handleCloseRecordingsModal}
      />

      <AssessmentHistoryModal
        open={assessmentsHistoryModalOpen}
        onClose={handleCloseAssessmentsHistoryModal}
      />

      <TeamAssessmentsModal
        open={teamAssessmentsModalOpen}
        onClose={handleCloseTeamAssessmentsModal}
      />

      {isInfoPanelOpen && (
        <Box
          className="fixed inset-0 z-40"
          onClick={handleCloseInfoPanel}
          sx={{ backgroundColor: "transparent" }}
        />
      )}
      <ChatInfoPanel
        isOpen={isInfoPanelOpen}
        onClose={handleCloseInfoPanel}
        anchorEl={infoPanelAnchorEl}
        initialTab={infoPanelInitialTab || undefined}
        onMessageSelect={onCollapseHeaderOnMessageSelect}
      />
    </Box>
  );
};
